import React, { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { Modal, Box, SelectChangeEvent, Stack } from '@mui/material'
import { NavLink, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import {
  addAuditsToAction,
  getAuditByActionId,
  removeAuditsFromAction,
} from '../../redux/audits/auditActions'
import { AppDispatch, RootState, useAppDispatch } from '../../redux/store'
import SelectAudit from './SelectAudit'
import UseAlert from '../../components/common/alert'
import {
  DetailButton,
  DetailEntityPanel,
  DetailTable,
} from '../../components/common/detail'
import {
  canManageMission,
  DetailRowActions,
} from '../../components/common/detail/DetailRowActions'
import { ListPageDescription } from '../../components/common/ListPageDescription'
import { formatTitle } from '../../utils/formatTitle'
import '../../components/common/ListPageTable.css'

interface AuditListProps {
  ActionId: string
  MissionId: string
}

const modalStyle = {
  position: 'absolute' as const,
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 420,
  bgcolor: 'background.paper',
  boxShadow: 24,
  p: 3,
  borderRadius: '12px',
}

const AuditListByAction: React.FC<AuditListProps> = ({
  ActionId,
  MissionId,
}) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const dispatch: AppDispatch = useAppDispatch()
  const roles = useSelector((state: any) => state.auth.roles)
  const auditsList = useSelector((state: RootState) => state.audit.sourceAudits)
  const loading = useSelector((state: RootState) => state.audit.loading)

  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const [SourceAudit, setSourceAudit] = useState<string[]>([])
  const [alert, setAlert] = useState({
    open: false,
    message: '',
    severity: '' as 'info' | 'success' | 'error' | 'warning',
  })

  useEffect(() => {
    if (ActionId) dispatch(getAuditByActionId(ActionId))
  }, [dispatch, ActionId])

  const rows = useMemo(() => {
    const list = Array.isArray(auditsList) ? auditsList : []
    const q = search.trim().toLowerCase()
    if (!q) return list
    return list.filter((a: any) =>
      (a.AuditName || '').toLowerCase().includes(q)
    )
  }, [auditsList, search])

  const handleDeleteClick = async (_id: string) => {
    try {
      await dispatch(
        removeAuditsFromAction({
          SourceAudit: { auditId: _id },
          _id: ActionId,
        })
      )
      dispatch(getAuditByActionId(ActionId))
      setAlert({
        open: true,
        message: t('alerts.auditDelete.success'),
        severity: 'success',
      })
    } catch {
      setAlert({
        open: true,
        message: t('alerts.auditDelete.error'),
        severity: 'error',
      })
    }
  }

  const handleAddAuditsToAction = async () => {
    try {
      if (SourceAudit.length && ActionId) {
        await dispatch(addAuditsToAction({ SourceAudit, _id: ActionId }))
        await dispatch(getAuditByActionId(ActionId))
        setAlert({
          open: true,
          message: t('alerts.auditUpdate.success'),
          severity: 'success',
        })
        setOpen(false)
        setSourceAudit([])
      } else {
        setAlert({
          open: true,
          message: t('alerts.auditUpdate.emptyFields'),
          severity: 'warning',
        })
      }
    } catch {
      setAlert({
        open: true,
        message: t('alerts.auditUpdate.error'),
        severity: 'error',
      })
    }
  }

  return (
    <>
      <UseAlert
        open={alert.open}
        message={alert.message}
        severity={alert.severity}
        onClose={() => setAlert((a) => ({ ...a, open: false }))}
      />
      <DetailEntityPanel
        listTitle={t('Audits List', { defaultValue: 'Audits list' })}
        searchPlaceholder={t('filterByName', { defaultValue: 'Filter by name' })}
        searchValue={search}
        onSearchChange={setSearch}
        addLabel={t('buttons.addAudit', { defaultValue: 'Add New Audit' })}
        onAdd={() => setOpen(true)}
        canAdd={canManageMission(roles)}
      >
        <DetailTable<any>
          loading={loading && !rows.length}
          loadingLabel={t('loading')}
          emptyTitle={t('missionDetailsPage.empty.title', {
            defaultValue: 'Nothing here yet',
          })}
          emptyMessage={t('taskDetailsPage.empty.audits', {
            defaultValue: 'No audit is linked to this task yet.',
          })}
          rows={rows}
          rowKey={(r) => (r._id || r.id) as string}
          columns={[
            {
              key: 'name',
              header: t('tables.auditTable.auditName', {
                defaultValue: 'Audit name',
              }),
              render: (r) => (
                <NavLink
                  to={`/audit-detail/${r._id}`}
                  className="incyia-name-link"
                >
                  <ListPageDescription
                    value={
                      r.AuditName ? formatTitle(r.AuditName) : undefined
                    }
                    tone="primary"
                  />
                </NavLink>
              ),
            },
            {
              key: 'desc',
              header: t('Description', { defaultValue: 'Description' }),
              render: (r) => <ListPageDescription value={r.Description} />,
            },
            {
              key: 'mission',
              header: t('Mission Name', { defaultValue: 'Mission name' }),
              render: (r) => (
                <ListPageDescription
                  value={
                    r.MissionName ? formatTitle(r.MissionName) : undefined
                  }
                />
              ),
            },
          ]}
          actionsHeader={t('Actions', { defaultValue: 'Action' })}
          renderActions={(r) => (
            <DetailRowActions
              roles={roles}
              onEdit={() => navigate(`/audit/${r._id}`)}
              onDelete={() => handleDeleteClick(r._id as string)}
              deleteItem={{
                entityType: t('audits', { defaultValue: 'Audit' }),
                name: r.AuditName ? formatTitle(r.AuditName) : undefined,
                details: [
                  {
                    label: t('deleteConfirm.fields.mission'),
                    value: r.MissionName
                      ? formatTitle(r.MissionName)
                      : '—',
                  },
                ],
              }}
            />
          )}
        />
      </DetailEntityPanel>

      <Modal open={open} onClose={() => setOpen(false)}>
        <Box sx={modalStyle}>
          <h4 style={{ margin: '0 0 16px' }}>{t('buttons.addAudit')}</h4>
          <Stack spacing={2}>
            <SelectAudit
              value={SourceAudit}
              onChange={(e: SelectChangeEvent<string[]>) =>
                setSourceAudit(e.target.value as string[])
              }
              MissionId={MissionId}
            />
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <DetailButton onClick={handleAddAuditsToAction}>
                {t('buttons.add')}
              </DetailButton>
              <DetailButton variant="ghost" onClick={() => setOpen(false)}>
                {t('buttons.cancel')}
              </DetailButton>
            </div>
          </Stack>
        </Box>
      </Modal>
    </>
  )
}

export default AuditListByAction
